/**
 * The small form a word's select opens when a person chooses "A new defect type…" (migration 0265; the server is
 * platform-core/internal/twin/org_defect_types.go, the rules lib/imports/defect-types). It names the org's own defect
 * type, says where in the route it is usually caused and, if the person knows it, which of the pack's defect families
 * it belongs to, and says plainly what a family means for the analysis. The Files page adds the type and maps the word
 * in one go; the import review adds the type and chooses it for the word, which is saved with the draft's approval.
 */
import { useId, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/lib/ui/button";
import { useToast } from "@/lib/ui/toast";
import { useOrg } from "@/lib/org-context";
import { useCreateDefectType, useVocabularyTargetsQuery } from "@/lib/api";
import {
  SEVERITY_CHOICES,
  createTypeForReview,
  defectTypeChoicesOf,
  defectTypeInputOf,
  familyNoteOf,
  newDefectTypeDraft,
  type DefectTypeChoices,
  type DefectTypeDraft,
} from "@/lib/imports/defect-types";

const FIELD_CLS =
  "w-full rounded-md border border-border bg-surface px-2 py-1 text-xs text-on-surface focus:border-brand focus:outline-none disabled:opacity-60";

/** The form, as a word's row shows it: the fields, what the family means, and the save. Its draft is its own. */
export function NewDefectTypeForm({
  word,
  choices,
  busy,
  submitLabel,
  onSubmit,
  onCancel,
}: {
  word: string;
  choices: DefectTypeChoices;
  busy: boolean;
  /** "Add and map" on the Files page; "Add" in the import review. */
  submitLabel: string;
  onSubmit: (draft: DefectTypeDraft) => void;
  onCancel: () => void;
}) {
  const id = useId();
  const [draft, setDraft] = useState<DefectTypeDraft>(() => newDefectTypeDraft(word));
  const set = (patch: Partial<DefectTypeDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const checked = defectTypeInputOf(draft);
  return (
    <div
      className="mt-1.5 flex flex-col gap-1.5 rounded-md border border-border bg-surface-muted/40 p-2"
      role="group"
      aria-label={`A new defect type for ${word}`}
      data-new-defect-type=""
    >
      <p className="text-[11px] text-on-surface-muted">
        A defect type of the org&apos;s own, for a defect the shared list does not have. {word} is mapped to it.
      </p>
      <label className="flex flex-col gap-0.5 text-[11px] text-on-surface-muted" htmlFor={`${id}-name`}>
        Name
        <input
          id={`${id}-name`}
          className={FIELD_CLS}
          value={draft.name}
          maxLength={200}
          disabled={busy}
          onChange={(ev) => set({ name: ev.target.value })}
        />
      </label>
      <label className="flex flex-col gap-0.5 text-[11px] text-on-surface-muted" htmlFor={`${id}-area`}>
        Where in the route it is usually caused
        <select
          id={`${id}-area`}
          className={FIELD_CLS}
          value={draft.area}
          disabled={busy}
          onChange={(ev) => set({ area: ev.target.value })}
        >
          <option value="">Choose an area</option>
          {choices.areas.map((a) => (
            <option key={a.key} value={a.key}>
              {a.label}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-0.5 text-[11px] text-on-surface-muted" htmlFor={`${id}-family`}>
        Family (optional)
        <select
          id={`${id}-family`}
          className={FIELD_CLS}
          value={draft.family}
          disabled={busy}
          onChange={(ev) => set({ family: ev.target.value })}
        >
          <option value="">No family</option>
          {choices.families.map((f) => (
            <option key={f.key} value={f.key}>
              {f.label}
            </option>
          ))}
        </select>
      </label>
      <p className="text-[11px] text-on-surface-muted" data-family-note="">
        {familyNoteOf(draft.family, choices.families)}
      </p>
      <label className="flex flex-col gap-0.5 text-[11px] text-on-surface-muted" htmlFor={`${id}-severity`}>
        Severity (optional)
        <select
          id={`${id}-severity`}
          className={FIELD_CLS}
          value={draft.severity}
          disabled={busy}
          onChange={(ev) => set({ severity: ev.target.value as DefectTypeDraft["severity"] })}
        >
          {SEVERITY_CHOICES.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </select>
      </label>
      {"error" in checked && draft.name.trim() !== "" && draft.area !== "" && (
        <p className="text-[11px] text-warning">{checked.error}</p>
      )}
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={busy || "error" in checked}
          onClick={() => onSubmit(draft)}
          aria-label={`${submitLabel}: ${word}`}
        >
          {busy && <Loader2 className="mr-1 size-3.5 animate-spin" />}
          {submitLabel}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/**
 * The form for a word of a draft under review: it reads the areas and families itself, adds the type, and chooses it
 * for the word, which the review saves with the draft's approval. A role that may not change the org's model is told
 * so instead.
 */
export function NewDefectTypeForReview({
  word,
  onChosen,
  onCancel,
}: {
  word: string;
  onChosen: (key: string) => void;
  onCancel: () => void;
}) {
  const { can } = useOrg();
  const targets = useVocabularyTargetsQuery();
  const create = useCreateDefectType();
  const toast = useToast();
  const choices = defectTypeChoicesOf(targets.data);
  if (!can("model.write")) {
    return (
      <p className="mt-1 text-[11px] text-warning">
        Only someone who can edit the plant model can add a defect type.
      </p>
    );
  }
  if (!choices) {
    return (
      <p className="mt-1 text-[11px] text-on-surface-muted">
        {targets.isLoading ? "Loading the areas and families…" : "This server cannot add defect types yet."}
      </p>
    );
  }
  return (
    <NewDefectTypeForm
      word={word}
      choices={choices}
      busy={create.isPending}
      submitLabel="Add"
      onCancel={onCancel}
      onSubmit={(draft) =>
        void createTypeForReview(word, draft, {
          create: (input) => create.mutateAsync(input),
          choose: onChosen,
          notify: (kind, message) => toast.push(kind, message),
        })
      }
    />
  );
}
